import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowRightIcon, CheckCircle2Icon, PencilIcon } from 'lucide-react';
import { usePortal } from '../../../contexts/PortalContext';
import { buildReview } from '../../../utils/review';
export function ReviewStep() {
  const {
    answers
  } = usePortal();
  const navigate = useNavigate();
  const sections = useMemo(() => buildReview(answers), [answers]);
  const issues = sections.flatMap(s => s.issues.map(i => ({
    ...i,
    section: s.title
  })));
  const edit = (step: string) => navigate(`/apply/${step}?from=review`);
  return <div data-id="element-366" data-mp="2adfupsreh6t8">
      <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.025em] text-ink-900 md:text-[34px]" data-id="element-367" data-mp="2adpolrfkfy47">Review your application</h1>
      <p className="mt-3 text-base text-ink-500 md:text-[17px]" data-id="element-368" data-mp="1hrmwzxkyoidb">Take a minute to check everything. You can edit any section.</p>

      {issues.length > 0 ? <motion.section initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-8 rounded-2xl bg-critical-50 p-5 ring-1 ring-critical-500/20" aria-labelledby="issues-title" data-id="element-369" data-mp="1mnllrma7c2e9">
          <h2 id="issues-title" className="flex items-center gap-2 font-semibold text-critical-700" data-id="element-370" data-mp="1dgi63mts7dek">
            <AlertCircleIcon className="h-5 w-5" aria-hidden data-id="element-371" data-mp="18l2wj72t43ny" />
            {issues.length} {issues.length === 1 ? 'thing needs' : 'things need'} your attention
          </h2>
          <ul className="mt-3 space-y-1" data-id="element-372" data-mp="yqj9hohvka17">
            {issues.map(i => <li key={i.message} data-id="element-373" data-mp="tpdgdpkaykr5">
                <button onClick={() => edit(i.step)} className="group flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-critical-700 transition-colors duration-150 hover:bg-white" data-id="element-374" data-mp="ts9b750ustbd">
                  <span data-id="element-375" data-mp="2k2awng6f6xmb">
                    <span className="font-medium" data-id="element-376" data-mp="2k1jcr2m6qobv">{i.section}:</span> {i.message}
                  </span>
                  <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden data-id="element-377" data-mp="2f4j3yjxyb7h2" />
                </button>
              </li>)}
          </ul>
        </motion.section> : <motion.div initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-8 flex items-center gap-3 rounded-2xl bg-positive-50 p-5 text-positive-700" data-id="element-378" data-mp="330ny9n95jp50">
          <CheckCircle2Icon className="h-5 w-5" aria-hidden data-id="element-379" data-mp="2u716s9bz981m" />
          <p className="font-medium" data-id="element-380" data-mp="3hvu346wihhwr">Everything looks complete. You’re ready to continue.</p>
        </motion.div>}

      <div className="mt-8 space-y-3" data-id="element-381" data-mp="3lwe1tewqgtl0">
        {sections.map((s, idx) => {
        const bad = s.issues.length > 0;
        return <motion.section key={s.id} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          delay: Math.min(idx * 0.04, 0.3),
          ease: [0.23, 1, 0.32, 1]
        }} className={`rounded-2xl bg-white ring-1 ${bad ? 'ring-2 ring-critical-500/60' : 'ring-ink-200'}`} aria-labelledby={`rev-${s.id}`} data-id="element-382" data-mp="380a77khy6j80" data-mpi={idx}>
              <header className="flex items-center gap-3 px-5 pt-4" data-id="element-383" data-mp="1ozrhvepiuame">
                {bad ? <AlertCircleIcon className="h-5 w-5 text-critical-500" aria-label="Needs attention" data-id="element-384" data-mp="1p1ktl8ezrl19" /> : <CheckCircle2Icon className="h-5 w-5 text-positive-500" aria-label="Complete" data-id="element-385" data-mp="22wwtrvbx23pk" />}
                <h2 id={`rev-${s.id}`} className="flex-1 font-semibold text-ink-900" data-id="element-386" data-mp="1xxtoqavxrxhl">
                  {s.title}
                </h2>
                <button onClick={() => edit(s.editStep)} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-pulse-700 transition-colors duration-150 hover:bg-pulse-50" data-id="element-387" data-mp="169a4qih69nm5">
                  <PencilIcon className="h-3.5 w-3.5" aria-hidden data-id="element-388" data-mp="1f96me0559m7p" /> Edit
                </button>
              </header>
              <dl className="grid gap-x-6 gap-y-3 px-5 pb-5 pt-3 sm:grid-cols-2" data-id="element-389" data-mp="rijcj9bo4mii">
                {s.rows.map(r => <div key={r.label} className="min-w-0" data-id="element-390" data-mp="mkhi5s8sl0rq">
                    <dt className="text-[13px] text-ink-500" data-id="element-391" data-mp="1089hxjd9p4wm">{r.label}</dt>
                    <dd className={`mt-0.5 truncate text-[15px] ${r.value ? 'text-ink-900' : 'font-medium text-critical-700'}`} data-id="element-392" data-mp="156ku40r1bri0">{r.value || 'Missing'}</dd>
                  </div>)}
              </dl>
              {bad && <div className="border-t border-critical-500/15 px-5 py-3" data-id="element-393" data-mp="1ex5zc717ru9w">
                  {s.issues.map(i => <p key={i.message} className="text-sm text-critical-700" data-id="element-394" data-mp="1ixpvb7a1wrug">
                      {i.message}
                    </p>)}
                </div>}
            </motion.section>;
      })}
      </div>
    </div>;
}
export function useReviewIssues(): number {
  const {
    answers
  } = usePortal();
  return useMemo(() => buildReview(answers).reduce((n, s) => n + s.issues.length, 0), [answers]);
}